/** Browser helpers for chat message Actions (no extra deps). */

// ponytail: module singleton so every bubble footer shares one TTS session
let speakingText: string | null = null;
const speakingListeners = new Set<() => void>();

function emitSpeaking(): void {
  speakingListeners.forEach((l) => l());
}

function setSpeakingText(next: string | null): void {
  if (speakingText === next) return;
  speakingText = next;
  emitSpeaking();
}

export function subscribeSpeaking(onStoreChange: () => void): () => void {
  speakingListeners.add(onStoreChange);
  return () => {
    speakingListeners.delete(onStoreChange);
  };
}

export function getSpeakingText(): string | null {
  return speakingText;
}

export function speakText(text: string, lang = 'en-US'): void {
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    throw new Error('Speech synthesis unavailable');
  }
  const trimmed = text.trim();
  if (!trimmed) return;

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(trimmed);
  utterance.lang = lang;
  utterance.onend = () => {
    if (speakingText === trimmed) setSpeakingText(null);
  };
  utterance.onerror = () => {
    if (speakingText === trimmed) setSpeakingText(null);
  };
  setSpeakingText(trimmed);
  window.speechSynthesis.speak(utterance);
}

export function stopSpeaking(): void {
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
  setSpeakingText(null);
}

/** Same message → stop; other/none → cancel previous (if any) and start. */
export function toggleSpeakText(text: string, lang = 'en-US'): 'started' | 'stopped' {
  const trimmed = text.trim();
  if (!trimmed) return 'stopped';
  if (speakingText === trimmed) {
    stopSpeaking();
    return 'stopped';
  }
  speakText(trimmed, lang);
  return 'started';
}

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult:
    ((ev: { results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }> }) => void) | null;
  onerror: ((ev: { error: string }) => void) | null;
  onend: (() => void) | null;
};

export function getSpeechRecognition(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}
